import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Controller, FormProvider, useFormContext, useFormState, } from 'react-hook-form';
import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Label } from './label';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
const Form = FormProvider;
const FormFieldContext = React.createContext({});
const FormField = ({ ...props }) => {
    return (_jsx(FormFieldContext.Provider, { value: { name: props.name }, children: _jsx(Controller, { ...props }) }));
};
const useFormField = () => {
    const fieldContext = React.useContext(FormFieldContext);
    const itemContext = React.useContext(FormItemContext);
    const { getFieldState } = useFormContext();
    const formState = useFormState({ name: fieldContext.name });
    const fieldState = getFieldState(fieldContext.name, formState);
    if (!fieldContext) {
        throw new Error('useFormField should be used within <FormField>');
    }
    const { id } = itemContext;
    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState,
    };
};
const FormItemContext = React.createContext({});
function FormItem({ className, ...props }) {
    const id = React.useId();
    return (_jsx(FormItemContext.Provider, { value: { id }, children: _jsx("div", { "data-slot": "form-item", className: cn('grid gap-2', className), ...props }) }));
}
/**
 * Messaggio dell'errore di un campo. Un campo array (elenco di email) con un
 * elemento non valido ha l'errore sull'elemento (`emails.1`), non sul campo:
 * senza ripiego il tooltip diceva «undefined». Si prende il primo messaggio
 * annidato.
 */
export function resolveFieldErrorMessage(error) {
    if (!error || typeof error !== 'object')
        return undefined;
    const { message } = error;
    if (typeof message === 'string' && message)
        return message;
    for (const [key, nested] of Object.entries(error)) {
        if (key === 'ref' || key === 'message' || key === 'type')
            continue;
        const nestedMessage = resolveFieldErrorMessage(nested);
        if (nestedMessage)
            return nestedMessage;
    }
    return undefined;
}
const INTERACTIVE_LABEL_CONTENT = 'a[href], button, input, select, textarea';
/**
 * L'accessorio del label (tooltip informativo, badge, asterisco) sta dentro
 * `<label for>`: un clic su di esso attiverebbe il campo (per il date-picker
 * aprirebbe il calendario). Si annulla l'attivazione del label, tranne quando
 * il clic parte da un contenuto interattivo (link, bottone): lì il browser non
 * attiva il label e l'azione di default (es. la navigazione) deve restare.
 */
function preventLabelActivation(event) {
    const target = event.target;
    if (target.closest(INTERACTIVE_LABEL_CONTENT))
        return;
    event.preventDefault();
}
function FormLabel({ className, children, endAdornment, trailing, ...props }) {
    const { error, formItemId, formMessageId } = useFormField();
    // Nessun namespace: i messaggi di validazione sono chiavi dell'app.
    const { t } = useTranslation();
    const rawErrorMessage = resolveFieldErrorMessage(error);
    const errorMessage = rawErrorMessage ? t(rawErrorMessage) : undefined;
    // Layout: testo + `endAdornment` (es. asterisco `*` di obbligatorietà)
    // inline a sinistra; icona errore con tooltip pushata all'estrema
    // destra del label tramite `justify-between`. Quando non c'è errore
    // l'icona scompare; l'asterisco resta accanto al testo.
    return (_jsxs("div", { "data-slot": "form-label", className: "flex items-center justify-between gap-1", children: [_jsxs(Label, { "data-error": !!error, className: cn('inline-flex items-center gap-1 shrink min-w-0', error && 'text-destructive', className), htmlFor: formItemId, ...props, children: [_jsx("span", { children: children }), endAdornment && (_jsx("span", { "data-slot": "form-label-adornment", className: "inline-flex items-center shrink-0", onClick: preventLabelActivation, children: endAdornment }))] }), trailing, error && (_jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: _jsxs("button", { type: "button", tabIndex: -1, className: "inline-flex shrink-0 cursor-help focus:outline-none", id: formMessageId, role: "alert", "aria-label": errorMessage, children: [_jsx(CircleAlert, { className: "size-4 text-destructive", "aria-hidden": "true" }), _jsx("span", { className: "sr-only", children: errorMessage })] }) }), _jsx(TooltipContent, { side: "top", children: _jsx("p", { className: "text-destructive font-medium text-xs", children: errorMessage }) })] }))] }));
}
function FormControl({ ...props }) {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
    return (_jsx(Slot, { "data-slot": "form-control", id: formItemId, "aria-describedby": !error
            ? `${formDescriptionId}`
            : `${formDescriptionId} ${formMessageId}`, "aria-invalid": !!error, ...props }));
}
/**
 * Messaggio d'errore di un campo SENZA riga del label (celle, orari): l'icona
 * col tooltip vive in `FormLabel`, quindi senza label mancherebbe anche il
 * nodo `formMessageId` a cui punta `aria-describedby` di `FormControl`.
 * `visuallyHidden` lo lascia ai soli lettori di schermo (griglie dense che
 * non devono cambiare altezza): in quel caso niente `role="alert"`, che in
 * una griglia annuncerebbe ogni errore nascosto, perché basta il nodo letto
 * da `aria-describedby`. Senza errore non rende nulla.
 */
function FormFieldMessage({ className, visuallyHidden = false, }) {
    const { error, formMessageId } = useFormField();
    // Nessun namespace: i messaggi di validazione sono chiavi dell'app.
    const { t } = useTranslation();
    if (!error)
        return null;
    const message = resolveFieldErrorMessage(error);
    return (_jsx("p", { "data-slot": "form-field-message", id: formMessageId, role: visuallyHidden ? undefined : 'alert', className: cn(visuallyHidden ? 'sr-only' : 'mt-1 text-xs text-destructive', className), children: message ? t(message) : null }));
}
function FormDescription({ className, ...props }) {
    const { formDescriptionId } = useFormField();
    return (_jsx("p", { "data-slot": "form-description", id: formDescriptionId, className: cn('text-muted-foreground text-sm', className), ...props }));
}
export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormField, FormFieldMessage, };
//# sourceMappingURL=form.js.map